import { LogIn, LogOut, Settings, UserRound } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { AccountSettingsDialog, SetPasswordDialog } from "@/components/shell/AccountSettingsDialog";
import { BillingButton } from "@/components/shell/BillingButton";
import { SupabaseAuthForm } from "@/components/shell/SupabaseAuthForm";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger, Tip } from "@/components/ui/overlays";
import { accounts, type AccountUser, type Accounts } from "@/lib/accounts";
import { api, queryClient } from "@/lib/api";
import { truncate } from "@/lib/utils";

/** Sign-in control (M2). Hidden entirely until the backend reports accounts
 * are configured. Signing in/out switches the identity every request runs
 * as, so the whole query cache is dropped both ways.
 *
 * @param hideBilling - Suppress the inline `BillingButton` ("Upgrade"/"Pro").
 * Used by the desktop `appshell/TopBar`, which already shows the single
 * canonical `UpgradeButton` — without this, both rendered side by side,
 * the "two Upgrade buttons" the docview rearchitecture retired. Compact's
 * `shell/TopBar` has no `UpgradeButton` of its own, so it omits this prop and
 * keeps `BillingButton` as its only upgrade entry point. */
export function AccountChip({ hideBilling = false }: { hideBilling?: boolean } = {}) {
  const [accs, setAccs] = useState<Accounts | null>(null);
  const [user, setUser] = useState<AccountUser | null>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  // The last uid we saw. `undefined` = "no callback yet"; `null` = signed out.
  const lastUidRef = useRef<string | null | undefined>(undefined);

  // Shared post-sign-in hint, independent of which provider signed the user
  // in: a fresh account with an empty atlas + local data present points at
  // the one-command migration.
  const afterSignIn = async (u: AccountUser) => {
    toast.success(`Signed in as ${u.email ?? u.uid}`);
    try {
      const docs = await api.request<unknown[]>("GET", "/api/documents");
      if (docs.length === 0) {
        toast(`Bring your local atlas with you?`, {
          duration: 15_000,
          description: `Run: python -m graphmind rekey-user local ${u.uid}`,
        });
      }
    } catch {
      /* purely a hint */
    }
  };

  useEffect(() => {
    let unsub: (() => void) | undefined;
    let unsubRecovery: (() => void) | undefined;
    let cancelled = false;
    void accounts().then((a) => {
      if (cancelled || !a) return;
      setAccs(a);
      // Browser returned from a password-reset email → prompt for a new password.
      unsubRecovery = a.onPasswordRecovery?.(() => setRecoveryOpen(true));
      unsub = a.onUser((u) => {
        setUser(u);
        // Refetch the whole cache ONLY on a real identity change (sign-in,
        // sign-out, account switch). onUser is Firebase onIdTokenChanged, which
        // also fires on the SDK's silent hourly token refresh with the SAME
        // uid — invalidating everything (incl. the full graph) then was pure
        // waste. The first callback is skipped too: the queries mounting with
        // this effect already fetch under the current identity.
        const uid = u?.uid ?? null;
        if (lastUidRef.current !== undefined && lastUidRef.current !== uid) {
          void queryClient.invalidateQueries();
        }
        lastUidRef.current = uid;
      });
    });
    return () => {
      cancelled = true;
      unsub?.();
      unsubRecovery?.();
    };
  }, []);

  if (!accs) return null;

  if (!user) {
    // Supabase gets email/password + Google (a compact popover form);
    // Firebase stays the single Google-popup button it always was.
    if (accs.provider === "supabase") {
      return (
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="sm">
              <LogIn className="h-3.5 w-3.5" /> Sign in
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end">
            <SupabaseAuthForm
              accs={accs}
              compact
              onSignedIn={(u) => {
                setPopoverOpen(false);
                void afterSignIn(u);
              }}
            />
          </PopoverContent>
        </Popover>
      );
    }
    return (
      <Tip label="Sign in with Google — your atlas syncs to your account identity">
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            void accs
              .signInWithGoogle()
              .then((u) => u && afterSignIn(u))
              .catch((e) => toast.error("Sign-in failed", { description: String(e) }))
          }
        >
          <LogIn className="h-3.5 w-3.5" /> Sign in
        </Button>
      </Tip>
    );
  }

  return (
    <div className="flex items-center gap-1">
      {!hideBilling && <BillingButton />}
      <Popover open={menuOpen} onOpenChange={setMenuOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm">
            {user.photoURL ? (
              <img src={user.photoURL} alt="" className="h-4 w-4 rounded-full" />
            ) : (
              <UserRound className="h-3.5 w-3.5" />
            )}
            <span className="max-w-[120px] truncate">
              {truncate(user.displayName ?? user.email ?? user.uid, 18)}
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="flex w-44 flex-col gap-1 p-1">
          <button
            type="button"
            className="flex items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-border/40"
            onClick={() => {
              setMenuOpen(false);
              setSettingsOpen(true);
            }}
          >
            <Settings className="h-3.5 w-3.5" /> Account settings
          </button>
          <button
            type="button"
            className="flex items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-border/40"
            onClick={() => {
              setMenuOpen(false);
              void accs.signOut().then(() => toast(`Signed out — back to the local atlas`));
            }}
          >
            <LogOut className="h-3.5 w-3.5 text-muted-foreground" /> Sign out
          </button>
        </PopoverContent>
      </Popover>

      <AccountSettingsDialog accs={accs} open={settingsOpen} onOpenChange={setSettingsOpen} />
      {accs.onPasswordRecovery && (
        <SetPasswordDialog accs={accs} open={recoveryOpen} onOpenChange={setRecoveryOpen} />
      )}
    </div>
  );
}
